import { createTheme, alpha, Shadows } from "@mui/material/styles";

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    lightBlue: true;
    success: true;
  }
}

declare module "@mui/material/Pagination" {
  interface PaginationPropsColorOverrides {
    lightBlue: true;
    info: true;
  }
}

declare module "@mui/material/PaginationItem" {
  interface PaginationItemPropsColorOverrides {
    lightBlue: true;
    info: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    lightBlue?: string;
    goldenrod?: string;
  }

  interface PaletteOptions {
    lightBlue?: string;
    goldenrod?: string;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme({
  palette: {},
});

const customShadows: Shadows = [...defaultTheme.shadows];

export const brand = {
  50: "hsl(210, 100%, 95%)",
  100: "hsl(210, 100%, 92%)",
  200: "hsl(210, 100%, 80%)",
  300: "hsl(210, 100%, 65%)",
  400: "hsl(210, 98%, 48%)",
  500: "hsl(210, 98%, 42%)",
  600: "hsl(210, 98%, 55%)",
  700: "hsl(210, 100%, 35%)",
  800: "hsl(210, 100%, 16%)",
  900: "hsl(210, 100%, 21%)",
};

export const gray = {
  50: "hsl(220, 35%, 97%, 1)",
  100: "hsl(220, 30%, 94%, 1)",
  200: "hsl(220, 20%, 88%, 1)",
  300: "hsl(220, 20%, 80%, 1)",
  400: "hsl(220, 20%, 65%, 1)",
  500: "hsl(220, 20%, 42%, 1)",
  600: "hsl(220, 20%, 35%, 1)",
  700: "hsl(220, 20%, 25%, 1)",
  800: "hsl(220, 30%, 6%, 1)",
  900: "hsl(220, 35%, 3%, 1)",
};

export const green = {
  50: "hsl(120, 80%, 98%)",
  100: "hsl(120, 75%, 94%)",
  200: "hsl(120, 75%, 87%)",
  300: "hsl(120, 61%, 77%)",
  400: "hsl(120, 44%, 53%)",
  500: "hsl(120, 59%, 30%)",
  600: "hsl(120, 70%, 25%)",
  700: "hsl(120, 75%, 16%)",
  800: "hsl(120, 84%, 10%)",
  900: "hsl(120, 87%, 6%)",
};

export const orange = {
  50: "hsl(45, 100%, 97%)",
  100: "hsl(45, 92%, 90%)",
  200: "hsl(45, 94%, 80%)",
  300: "hsl(45, 90%, 65%)",
  400: "hsl(45, 90%, 40%)",
  500: "hsl(45, 90%, 35%)",
  600: "hsl(45, 91%, 25%)",
  700: "hsl(45, 94%, 20%)",
  800: "hsl(45, 95%, 16%)",
  900: "hsl(45, 93%, 12%)",
};

export const red = {
  50: "hsl(0, 100%, 97%)",
  100: "hsl(0, 92%, 90%)",
  200: "hsl(0, 94%, 80%)",
  300: "hsl(0, 90%, 65%)",
  400: "hsl(0, 90%, 40%)",
  500: "hsl(0, 90%, 30%)",
  600: "hsl(0, 91%, 25%)",
  700: "hsl(0, 94%, 18%)",
  800: "hsl(0, 95%, 12%)",
  900: "hsl(0, 93%, 6%)",
};

export const lightBlue = {
  50: "hsl(197, 98%, 97%)",
  100: "hsl(197, 90%, 90%)",
  200: "hsl(197, 92%, 80%)",
  300: "hsl(197, 88%, 65%)",
  400: "hsl(197, 88%, 40%)",
  500: "hsl(197, 88%, 30%)",
  600: "hsl(197, 89%, 25%)",
  700: "hsl(197, 92%, 18%)",
  800: "hsl(197, 93%, 12%)",
  900: "hsl(197, 91%, 6%)",
};

export const colorSchemes = {
  light: {
    palette: {
      lightBlue: {
        contrastText: lightBlue[100],
        light: lightBlue[200],
        main: lightBlue[400],
        dark: lightBlue[700],
      },
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        contrastText: red[100],
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        contrastText: green[50],
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[300], 0.4),
      background: {
        default: "hsl(0, 0%, 99%)",
        paper: "hsl(220, 35%, 97%)",
        boxWrapper: "rgb(255,255,255)",
        box: "rgb(240,242,245)",
        textInput: "rgb(255, 255, 255)",
        boxGrey: "hsl(0, 0%, 99%)",
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
        form: "hsl(220, 30%, 7%)",
      },
      // action: {
      //   hover: alpha(gray[200], 0.2),
      //   selected: `${alpha(gray[200], 0.3)}`,
      //   active: alpha(gray[200], 0.2),
      // },
      baseShadow:
        "hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px",
    },
  },
  dark: {
    palette: {
      lightBlue: {
        contrastText: lightBlue[200],
        light: lightBlue[300],
        main: lightBlue[400],
        dark: lightBlue[700],
      },
      primary: {
        contrastText: brand[50],
        light: brand[300],
        main: brand[400],
        dark: brand[700],
      },
      info: {
        contrastText: brand[100],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        contrastText: red[200],
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        contrastText: green[300],
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        default: gray[900],
        paper: "hsl(220, 30%, 7%)",
        boxWrapper: "rgb(37, 39, 40)",
        box: "rgb(51, 51, 52)",
        textInput: "rgb(72, 72, 72)",
        boxGrey: gray[100],
      },
      text: {
        primary: "hsl(0, 0%, 100%)",
        secondary: gray[400],
        form: "hsl(220, 30%, 7%)",
      },
      action: {
        hover: alpha(gray[200], 0.4),
        selected: alpha(gray[200], 0.6),
        active: alpha(gray[200], 0.6),
      },
      baseShadow:
        "hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px",
    },
  },
};

export const typography = {
  color: "textPrimary",
  fontFamily: "Inter, sans-serif",
  h1: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(18),
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

// @ts-ignore
const defaultShadows: Shadows = [
  "none",
  "var(--template-palette-baseShadow)",
  ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;
